import React, { memo, useCallback } from 'react'
import { AlertTriangle, Edit2, EyeOff, GripVertical, Save, Trash2, X } from 'lucide-react'
import { BookingCustomField, BookingCustomFieldType } from '@xeetr/types/models'
import { CSS } from '@dnd-kit/utilities'
import { useSortable } from '@dnd-kit/sortable'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

export interface FieldInfo {
  id: string
  field: BookingCustomField
  index: number
  isEditing: boolean
}

export interface EditingState {
  name: string
  setName: (value: string) => void
  description: string
  setDescription: (value: string) => void
  required: boolean
  setRequired: (value: boolean) => void
  hidden: boolean
  setHidden: (value: boolean) => void
  customerVisible: boolean
  setCustomerVisible: (value: boolean) => void
  type: BookingCustomFieldType
  setType: (value: BookingCustomFieldType) => void
  options: string
  setOptions: (value: string) => void
  canSave?: boolean
}

export interface FieldActions {
  onEdit: () => void
  onDelete: () => void
  onSave: () => void
  onCancel: () => void
}

export interface SortableCustomFieldProps {
  fieldInfo: FieldInfo
  editingState: EditingState
  fieldActions: FieldActions
}

const FIELD_TYPE_LABELS: Record<BookingCustomFieldType, string> = {
  text: 'Text',
  number: 'Nummer',
  boolean: 'Ja/Nein',
  enum: 'Auswahl',
  date: 'Datum',
}

const SortableCustomFieldComponent: React.FC<SortableCustomFieldProps> = ({
  fieldInfo,
  editingState,
  fieldActions,
}) => {
  const { id, field, index, isEditing } = fieldInfo
  const {
    name: editingFieldName,
    setName: setEditingFieldName,
    description: editingFieldDescription,
    setDescription: setEditingFieldDescription,
    required: editingFieldRequired,
    setRequired: setEditingFieldRequired,
    hidden: editingFieldHidden,
    setHidden: setEditingFieldHidden,
    customerVisible: editingFieldCustomerVisible,
    setCustomerVisible: setEditingFieldCustomerVisible,
    type: editingFieldType,
    setType: setEditingFieldType,
    options: editingFieldOptions,
    setOptions: setEditingFieldOptions,
    canSave,
  } = editingState
  const {
    onEdit: onEditField,
    onDelete: onDeleteField,
    onSave: onSaveField,
    onCancel: onCancelEdit,
  } = fieldActions
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setEditingFieldName(e.target.value)
    },
    [setEditingFieldName]
  )

  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setEditingFieldDescription(e.target.value)
    },
    [setEditingFieldDescription]
  )

  const handleTypeChange = useCallback(
    (value: BookingCustomFieldType) => {
      setEditingFieldType(value)
    },
    [setEditingFieldType]
  )

  const handleOptionsChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setEditingFieldOptions(e.target.value)
    },
    [setEditingFieldOptions]
  )

  const handleRequiredChange = useCallback(
    (checked: boolean) => {
      setEditingFieldRequired(checked)
    },
    [setEditingFieldRequired]
  )

  const handleHiddenChange = useCallback(
    (checked: boolean) => {
      setEditingFieldHidden(checked)
    },
    [setEditingFieldHidden]
  )

  const handleCustomerVisibleChange = useCallback(
    (checked: boolean) => {
      setEditingFieldCustomerVisible(checked)
    },
    [setEditingFieldCustomerVisible]
  )

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className="rounded-lg border p-4 transition-shadow hover:shadow-md"
      role="listitem"
      aria-label={`Benutzerdefiniertes Feld: ${field.name}`}
    >
      {isEditing ? (
        <div className="space-y-3" role="form" aria-label="Feld bearbeiten">
          <div>
            <Label htmlFor={`edit-field-name-${index}`} className="text-sm">
              Feldname
            </Label>
            <Input
              id={`edit-field-name-${index}`}
              value={editingFieldName}
              onChange={handleNameChange}
              placeholder="z.B. Allergien, Sonderwünsche"
              className="mt-1"
              aria-describedby={`name-error-${index}`}
            />
          </div>
          <div>
            <Label htmlFor={`edit-field-description-${index}`} className="text-sm">
              Beschreibung
            </Label>
            <Textarea
              id={`edit-field-description-${index}`}
              value={editingFieldDescription}
              onChange={handleDescriptionChange}
              placeholder="Beschreibung für das Feld"
              className="mt-1"
              rows={2}
            />
          </div>
          <div>
            <Label htmlFor={`edit-field-type-${index}`} className="text-sm">
              Feldtyp
            </Label>
            <Select value={editingFieldType} onValueChange={handleTypeChange}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="text">Text</SelectItem>
                <SelectItem value="number">Nummer</SelectItem>
                <SelectItem value="boolean">Ja/Nein</SelectItem>
                <SelectItem value="enum">Auswahl (Dropdown)</SelectItem>
                <SelectItem value="date">Datum</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {editingFieldType === 'enum' && (
            <div>
              <Label htmlFor={`edit-field-options-${index}`} className="text-sm">
                Auswahloptionen (durch Komma getrennt)
              </Label>
              <Input
                id={`edit-field-options-${index}`}
                value={editingFieldOptions}
                onChange={handleOptionsChange}
                placeholder="z.B. Option 1, Option 2, Option 3"
                className="mt-1"
              />
            </div>
          )}
          <div className="flex items-center space-x-2">
            <Switch
              id={`edit-field-required-${index}`}
              checked={editingFieldRequired}
              onCheckedChange={handleRequiredChange}
            />
            <Label htmlFor={`edit-field-required-${index}`} className="text-sm">
              Pflichtfeld
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <Switch
              id={`edit-field-hidden-${index}`}
              checked={editingFieldHidden}
              onCheckedChange={handleHiddenChange}
            />
            <Label htmlFor={`edit-field-hidden-${index}`} className="text-sm">
              Versteckt
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <Switch
              id={`edit-field-customer-visible-${index}`}
              checked={editingFieldCustomerVisible}
              onCheckedChange={handleCustomerVisibleChange}
            />
            <Label htmlFor={`edit-field-customer-visible-${index}`} className="text-sm">
              Im Kundenbereich anzeigen
            </Label>
          </div>
          <div className="flex space-x-2">
            <Button
              onClick={onSaveField}
              disabled={!canSave}
              size="sm"
              aria-label="Änderungen speichern"
            >
              <Save className="mr-2 h-4 w-4" />
              Speichern
            </Button>
            <Button
              onClick={onCancelEdit}
              variant="outline"
              size="sm"
              aria-label="Bearbeitung abbrechen"
            >
              <X className="mr-2 h-4 w-4" />
              Abbrechen
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <div className="truncate font-medium">{field.name}</div>
              <div className="flex items-center gap-1">
                {field.required && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <AlertTriangle className="h-4 w-4 text-orange-500" />
                    </TooltipTrigger>
                    <TooltipContent color="secondary">
                      <p>Pflichtfeld</p>
                    </TooltipContent>
                  </Tooltip>
                )}
                {field.hidden && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <EyeOff className="h-4 w-4 text-gray-500" />
                    </TooltipTrigger>
                    <TooltipContent color="secondary">
                      <p>Verstecktes Feld</p>
                    </TooltipContent>
                  </Tooltip>
                )}
                {field.customerVisible && (
                  <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800">
                    Kunden
                  </span>
                )}
              </div>
            </div>
            <div className="text-muted-foreground truncate text-sm">{field.description}</div>
            <div className="mt-1 flex flex-wrap gap-2">
              <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800">
                {FIELD_TYPE_LABELS[field.type]}
              </span>
            </div>
            {field.type === 'enum' && field.options && (
              <div className="text-muted-foreground mt-1 truncate text-xs">
                Optionen: {field.options.join(', ')}
              </div>
            )}
          </div>
          <div className="ml-2 flex space-x-1">
            <Button
              variant="ghost"
              size="sm"
              {...listeners}
              className="cursor-grab hover:cursor-grabbing focus:cursor-grabbing"
              aria-label="Feld verschieben"
            >
              <GripVertical className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onEditField}
              aria-label={`Feld ${field.name} bearbeiten`}
            >
              <Edit2 className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onDeleteField}
              className="text-red-600 hover:bg-red-50 hover:text-red-800"
              aria-label={`Feld ${field.name} löschen`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

export const SortableCustomField = memo(SortableCustomFieldComponent)
